import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import sodium from 'libsodium-wrappers';
import { jwtDecode } from 'jwt-decode';

import AppShell from './AppShell';
import { socket } from '../socket';

const ChatWindow = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 120px); // Adjust based on AppShell height
  padding: 16px;
`;

const MessagesContainer = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  margin-bottom: 16px;
`;

const Message = styled.div`
  padding: 8px 12px;
  border-radius: 18px;
  margin-bottom: 8px;
  max-width: 70%;
  word-wrap: break-word;
  background-color: ${props => (props.sent ? 'var(--tg-accent)' : 'var(--tg-surface)')};
  color: ${props => (props.sent ? '#fff' : 'var(--tg-text)')};
  align-self: ${props => (props.sent ? 'flex-end' : 'flex-start')};
  margin-left: ${props => (props.sent ? 'auto' : '0')};
  margin-right: ${props => (props.sent ? '0' : 'auto')};
`;

const InputForm = styled.form`
  display: flex;
  gap: 8px;
`;

const Input = styled.input`
  flex-grow: 1;
  padding: 12px;
  border-radius: var(--tg-radius-sm);
  border: 1px solid var(--tg-border);
  background: var(--tg-surface);
  color: var(--tg-text);
`;

const SendButton = styled.button`
  padding: 12px 18px;
  border-radius: var(--tg-radius-sm);
  border: none;
  background: var(--tg-primary);
  color: #fff;
  cursor: pointer;
`;

const PrivateChat = () => {
  const { username: recipientUsernameRaw } = useParams();
  const recipientUsername = useMemo(() => {
    if (!recipientUsernameRaw) return '';
    try {
      return decodeURIComponent(recipientUsernameRaw);
    } catch {
      return recipientUsernameRaw;
    }
  }, [recipientUsernameRaw]);
  const [newMessage, setNewMessage] = useState('');
  const [recipient, setRecipient] = useState(null);
  const [privateKey, setPrivateKey] = useState(null);
  const currentUser = useMemo(() => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      return jwtDecode(token);
    } catch {
      return null;
    }
  }, []);
  const [messages, setMessages] = useState(() => {
    const u = currentUser?.username;
    const chatHistoryKey = `chat_${recipientUsername}_${u}`;
    const inverseKey = `chat_${u}_${recipientUsername}`;
    const stored = localStorage.getItem(chatHistoryKey) || localStorage.getItem(inverseKey);
    return stored ? JSON.parse(stored) : [];
  });


  useEffect(() => {
    const initKeysAndRecipient = async () => {
      if (!currentUser) return;

      // 1. Load current user's private key
      const storedKey = localStorage.getItem(`privateKey_${currentUser.username}`);
      if (storedKey) {
        await sodium.ready;
        setPrivateKey(sodium.from_base64(storedKey, sodium.base64_variants.URLSAFE));
      }

      // 2. Find recipient's public key from localStorage
      const usersData = JSON.parse(localStorage.getItem('users_data') || '[]');
      const recipientUser = usersData.find(u => u.username === recipientUsername);
      if (recipientUser) {
        setRecipient(recipientUser);
      } else {
        console.error('Recipient not found in local user data');
      }
    };

    initKeysAndRecipient();
  }, [currentUser, recipientUsername]);


  useEffect(() => {
    const handleNewMessage = (event) => {
      const message = event.detail;
      if (message.sender === recipientUsername || message.recipient === recipientUsername) {
        setMessages(prev => [...prev, message]);
      }
    };

    window.addEventListener('new-private-message', handleNewMessage);
    return () => {
      window.removeEventListener('new-private-message', handleNewMessage);
    };
  }, [recipientUsername]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !recipient || !privateKey || !currentUser) return;

    try {
      await sodium.ready;
      const recipientPublicKey = sodium.from_base64(recipient.publicKey, sodium.base64_variants.URLSAFE);
      const nonce = sodium.randombytes_buf(sodium.crypto_box_NONCEBYTES);
      const ciphertext = sodium.crypto_box_easy(newMessage, nonce, recipientPublicKey, privateKey);

      const encryptedMessage = {
        ciphertext: sodium.to_base64(ciphertext, sodium.base64_variants.URLSAFE),
        nonce: sodium.to_base64(nonce, sodium.base64_variants.URLSAFE),
      };

      socket.emit('send-private-message', { recipientUsername, encryptedMessage });

      const message = { text: newMessage, sender: currentUser.username, recipient: recipientUsername, timestamp: Date.now() };
      const chatHistoryKey = `chat_${currentUser.username}_${recipientUsername}`;
      const inverseKey = `chat_${recipientUsername}_${currentUser.username}`;

      const existingHistory = JSON.parse(localStorage.getItem(chatHistoryKey) || localStorage.getItem(inverseKey) || '[]');
      existingHistory.push(message);
      localStorage.setItem(chatHistoryKey, JSON.stringify(existingHistory));

      setMessages(prev => [...prev, message]);
      setNewMessage('');
    } catch (error) {
      console.error('Encryption failed:', error);
    }
  };

  return (
    <AppShell title={`Chat with ${recipientUsername}`}>
      <ChatWindow>
        <MessagesContainer>
          {messages.map((msg, index) => (
            <Message key={index} sent={msg.sender === currentUser?.username}>
              {msg.text}
            </Message>
          ))}
        </MessagesContainer>
        <InputForm onSubmit={handleSendMessage}>
          <Input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
          />
          <SendButton type="submit">Send</SendButton>
        </InputForm>
      </ChatWindow>
    </AppShell>
  );
};

export default PrivateChat;
